import * as React from 'react';

import { cn, formatCompactNumber } from '@/lib/utils';
import { Stagger, RiseIn } from '@/components/ui/motion';

/**
 * Readout — the signature element of the interface, and the single metric
 * tile for the whole app.
 *
 * Every screen in this product ultimately shows a measured quantity against
 * an expected one: litres chargés vs livrés, stock théorique vs jaugé, quota
 * vs consommé, encaissé vs facturé. So the KPI is built as a gauge rather
 * than as a generic stat card: the figure is set in the display face at its
 * wide extreme with tabular figures (columns of readouts stay aligned digit
 * for digit), and when the value has a capacity it is drawn against it.
 *
 * `capacity` turns the tile into a gauge. `threshold` draws the line the
 * value is judged against — the point of the whole component.
 *
 * COLOUR MARKS STATE, NOT SLOT. Thirteen hand-rolled tiles used to colour by
 * position — tile 1 amber, tile 2 blue, tile 3 green — which made a row of
 * counts look like a row of alarms and left nothing to mark the one value
 * that was actually wrong. Only `positive`, `attention` and `critical` carry
 * colour here; everything else is neutral.
 */

const TONES = {
  neutral: { fill: 'bg-primary', text: 'text-foreground' },
  positive: { fill: 'bg-success', text: 'text-success' },
  attention: { fill: 'bg-flame', text: 'text-flame-ink' },
  critical: { fill: 'bg-destructive', text: 'text-destructive' },
};

/**
 * Tone names inherited from the tiles this component replaced. The colour
 * names (emerald/sky/amber/rose/violet/slate) described a hue, not a
 * meaning, so they collapse onto the four states — and the purely decorative
 * ones collapse to neutral.
 */
const TONE_ALIASES = {
  ok: 'positive',
  emerald: 'positive',
  green: 'positive',
  success: 'positive',
  warning: 'attention',
  amber: 'attention',
  orange: 'attention',
  risk: 'critical',
  rose: 'critical',
  red: 'critical',
  destructive: 'critical',
  info: 'neutral',
  sky: 'neutral',
  blue: 'neutral',
  violet: 'neutral',
  purple: 'neutral',
  slate: 'neutral',
  gray: 'neutral',
};

const resolveTone = (tone, up) => {
  if (typeof up === 'boolean') return up ? 'positive' : 'critical';
  if (!tone) return 'neutral';
  return TONES[tone] ? tone : TONE_ALIASES[tone] || 'neutral';
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') {
    // Au-delà du million, l'abrégé (« 163 M ») tient dans la tuile; la valeur
    // exacte reste lisible dans l'infobulle.
    if (Math.abs(value) >= 1_000_000) return formatCompactNumber(value);
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(value);
  }
  return value;
};

/** Valeur exacte, pour l'infobulle d'un nombre affiché en abrégé. */
const exactValue = (value) =>
  typeof value === 'number' && Math.abs(value) >= 1_000_000
    ? new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(value)
    : undefined;

const Readout = ({
  label,
  // Prop aliases kept from the tiles this replaced, so the migration was a
  // rename rather than a rewrite of every call site.
  title,
  value,
  unit,
  hint,
  detail,
  subtitle,
  icon: Icon,
  tone,
  color,
  up,
  capacity,
  threshold,
  size = 'default',
  panel = false,
  className,
  ref,
  ...props
}) => {
  const resolvedLabel = label ?? title;
  const resolvedHint = hint ?? detail ?? subtitle;
  const toneStyles = TONES[resolveTone(tone ?? color, up)];

  // A tile that navigates is a control, so it renders as a button and is
  // reachable by keyboard. Several of the tiles this replaced were already
  // buttons; the rest were clickable divs that Tab skipped over.
  const interactive = typeof props.onClick === 'function';
  const Tag = interactive ? 'button' : 'div';

  const numericValue = typeof value === 'number' ? value : null;
  const hasGauge =
    typeof capacity === 'number' && capacity > 0 && numericValue !== null;
  const pct = hasGauge
    ? Math.max(0, Math.min(100, (numericValue / capacity) * 100))
    : null;
  const thresholdPct =
    hasGauge && typeof threshold === 'number'
      ? Math.max(0, Math.min(100, (threshold / capacity) * 100))
      : null;

  return (
    <Tag
      ref={ref}
      type={interactive ? 'button' : undefined}
      className={cn(
        'flex min-w-0 flex-col justify-between gap-3 rounded-xl border p-3.5',
        panel
          ? 'readout-panel border-transparent'
          : 'border-border bg-card shadow-sm',
        interactive &&
          'cursor-pointer text-left transition-all duration-200 hover:-translate-y-px hover:border-primary/40 hover:shadow-md',
        className,
      )}
      {...props}
    >
      <div className="flex items-start justify-between gap-3">
        <p className={cn('readout-label', panel && 'text-readout-foreground/60')}>
          {resolvedLabel}
        </p>
        {Icon && (
          <Icon
            className={cn(
              'h-4 w-4 shrink-0',
              panel ? 'text-readout-foreground/50' : 'text-muted-foreground',
            )}
            aria-hidden="true"
          />
        )}
      </div>

      <div className="space-y-2">
        <p className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
          <span
            title={exactValue(value)}
            className={cn(
              'readout min-w-0 break-words [overflow-wrap:anywhere]',
              size === 'sm' && 'text-base md:text-lg',
              size === 'default' && 'text-lg md:text-xl',
              size === 'lg' && 'text-xl md:text-2xl',
              panel ? 'text-readout-foreground' : toneStyles.text,
            )}
          >
            {formatValue(value)}
          </span>
          {unit && (
            <span
              className={cn(
                'text-xs font-medium',
                panel ? 'text-readout-foreground/60' : 'text-muted-foreground',
              )}
            >
              {unit}
            </span>
          )}
        </p>

        {hasGauge && (
          <div
            className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted"
            role="meter"
            aria-valuenow={numericValue}
            aria-valuemin={0}
            aria-valuemax={capacity}
            aria-label={resolvedLabel}
          >
            <div
              className={cn('h-full rounded-full transition-[width] duration-500', toneStyles.fill)}
              style={{ width: `${pct}%` }}
            />
            {thresholdPct !== null && (
              // The line the value is read against.
              <span
                className="absolute inset-y-0 w-px bg-foreground/50"
                style={{ left: `${thresholdPct}%` }}
                aria-hidden="true"
              />
            )}
          </div>
        )}

        {resolvedHint && (
          <p
            className={cn(
              'text-xs',
              panel ? 'text-readout-foreground/60' : 'text-muted-foreground',
            )}
          >
            {resolvedHint}
          </p>
        )}
      </div>
    </Tag>
  );
};
Readout.displayName = 'Readout';

/**
 * Row of readouts. Collapses to two columns on phones, never to one.
 *
 * Tiles arrive one after the other rather than as a block: the eye is led
 * left to right in reading order, and the figures read as being taken rather
 * than pasted. 50ms apart — enough to perceive, too short to wait for.
 */
const ReadoutGrid = ({ className, children, ref, ...props }) => (
  <Stagger
    ref={ref}
    className={cn('grid grid-cols-2 gap-3 lg:grid-cols-4', className)}
    {...props}
  >
    {React.Children.map(children, (child) =>
      child ? <RiseIn>{child}</RiseIn> : child,
    )}
  </Stagger>
);
ReadoutGrid.displayName = 'ReadoutGrid';

export { Readout, ReadoutGrid };
